/* eslint-disable @typescript-eslint/no-use-before-define */
import { createContext, useContext } from 'react'
import type { PropsWithChildren } from 'react'

import { useDeleteUrlMutation } from '@Store/api/product'

import {
  BaseModal,
  type BaseModalDispatch,
  BaseModalProvider,
  type BaseModalState,
  useBaseModalDispatch,
  useBaseModalState,
} from './BaseModal'

interface RemoveUrlState extends BaseModalState {}

interface RemoveUrlDispatch extends BaseModalDispatch {
  deleteUrl: (payload?: any) => void
}

const RemoveUrlStateContext = createContext<RemoveUrlState>(
  {} as RemoveUrlState,
)
RemoveUrlStateContext.displayName = 'RemoveUrlState'

const RemoveUrlDispatchContext = createContext<RemoveUrlDispatch>(
  {} as RemoveUrlDispatch,
)
RemoveUrlDispatchContext.displayName = 'RemoveUrlDispatch'

function RemoveUrlProvider({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  return (
    <BaseModalProvider>
      <RemoveUrlProviderInner modalContent={modalContent}>
        {children}
      </RemoveUrlProviderInner>
    </BaseModalProvider>
  )
}

function RemoveUrlProviderInner({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  const baseState = useBaseModalState()
  const baseDispatch = useBaseModalDispatch()

  const [deleteUrl] = useDeleteUrlMutation()

  return (
    <RemoveUrlStateContext.Provider value={baseState}>
      <RemoveUrlDispatchContext.Provider value={{ ...baseDispatch, deleteUrl }}>
        <BaseModal isOpen={baseState.isOpen} close={baseDispatch.close}>
          {modalContent}
        </BaseModal>
        {children}
      </RemoveUrlDispatchContext.Provider>
    </RemoveUrlStateContext.Provider>
  )
}

function useRemoveUrlState(): RemoveUrlState {
  try {
    const context = useContext(RemoveUrlStateContext)
    return context
  } catch (error) {
    throw new Error('useRemoveUrlState must be used within a RemoveUrlProvider')
  }
}

function useRemoveUrlDispatch(): RemoveUrlDispatch {
  try {
    const context = useContext(RemoveUrlDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useRemoveUrlDispatch must be used within a RemoveUrlProvider',
    )
  }
}

export { RemoveUrlProvider, useRemoveUrlState, useRemoveUrlDispatch }
